import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAuditLogs } from '@/lib/api';

type AuditLog = {
    audit_id: number;
    actor_id: number | null;
    action: string;
    target_type?: string | null;
    target_id?: number | null;
    detail?: Record<string, any> | null;
    ip_address?: string | null;
    user_agent?: string | null;
    created_at: string;
};

export function AuditLogs() {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actorId, setActorId] = useState('');
    const [action, setAction] = useState('');
    const [targetType, setTargetType] = useState('');
    const [targetId, setTargetId] = useState('');
    const [limit, setLimit] = useState('50');

    // @ts-ignore
    const { theme, user } = useOutletContext<{ theme: any; user: any }>();

    const fetchLogs = async () => {
        setLoading(true);
        setError('');
        try {
            const params: any = {};
            if (actorId.trim()) params.actor_id = Number(actorId);
            if (action.trim()) params.action = action.trim();
            if (targetType.trim()) params.target_type = targetType.trim();
            if (targetId.trim()) params.target_id = Number(targetId);
            if (limit.trim()) params.limit = Number(limit);

            const res = await getAuditLogs(params);
            setLogs(res.data || []);
        } catch (err) {
            console.error(err);
            setError('ログの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role_type !== 'Admin') return;
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (!user) {
        return <div className="p-6 text-center opacity-70">Loading...</div>;
    }

    if (user.role_type !== 'Admin') {
        return <div className="p-6 text-center opacity-70">アクセス権限がありません。</div>;
    }

    return (
        <div className="max-w-6xl mx-auto mt-8 p-4">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-serif font-bold tracking-wider" style={{ color: theme?.colors?.text }}>
                        監査ログ
                    </h1>
                    <p className="text-sm opacity-70">管理操作の履歴を確認できます。</p>
                </div>
                <Button
                    onClick={fetchLogs}
                    disabled={loading}
                    style={{ backgroundColor: theme?.colors?.primary, color: '#fff' }}
                >
                    {loading ? '取得中...' : '更新'}
                </Button>
            </div>

            <div
                className="p-4 rounded-xl border backdrop-blur mb-6"
                style={{ backgroundColor: theme?.colors?.card + 'cc', borderColor: theme?.colors?.border }}
            >
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <Input
                        value={actorId}
                        onChange={(e) => setActorId(e.target.value)}
                        placeholder="actor_id"
                    />
                    <Input
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        placeholder="action"
                    />
                    <Input
                        value={targetType}
                        onChange={(e) => setTargetType(e.target.value)}
                        placeholder="target_type"
                    />
                    <Input
                        value={targetId}
                        onChange={(e) => setTargetId(e.target.value)}
                        placeholder="target_id"
                    />
                    <Input
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        placeholder="limit"
                    />
                </div>
                <div className="flex justify-end mt-3">
                    <Button
                        onClick={fetchLogs}
                        disabled={loading}
                        className="font-serif tracking-widest"
                        style={{ backgroundColor: theme?.colors?.primary, color: '#fff' }}
                    >
                        絞り込み
                    </Button>
                </div>
            </div>

            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

            <div className="space-y-3">
                {logs.length === 0 ? (
                    <div className="text-center opacity-60">ログはありません。</div>
                ) : (
                    logs.map((log) => (
                        <div
                            key={log.audit_id}
                            className="p-4 rounded-xl border backdrop-blur"
                            style={{ backgroundColor: theme?.colors?.card + '80', borderColor: theme?.colors?.border }}
                        >
                            <div className="flex flex-wrap items-center gap-3 text-xs opacity-80 mb-2">
                                <span>{new Date(log.created_at).toLocaleString()}</span>
                                <span>Actor: {log.actor_id ?? '-'}</span>
                                <span>Action: {log.action}</span>
                                <span>Target: {log.target_type || '-'}#{log.target_id ?? '-'}</span>
                                <span>IP: {log.ip_address || '-'}</span>
                            </div>
                            <div className="text-xs opacity-70 mb-2">
                                UA: {log.user_agent || '-'}
                            </div>
                            <div className="text-sm whitespace-pre-wrap">
                                {log.detail ? JSON.stringify(log.detail, null, 2) : 'detail: -'}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
